import axios from 'axios';

// In-memory access token storage
let accessToken = null;

// Function to set the access token from outside (e.g., AuthContext)
export const setAccessToken = (token) => {
  accessToken = token;
};

// Function to get the access token
export const getAccessToken = () => accessToken;

// Create an axios instance
const apiClient = axios.create({
  // The proxy in package.json will automatically forward this to http://localhost:5001
  baseURL: '/api',
  withCredentials: true, // Important for cookies (refresh token)
});

// Add a request interceptor to include the token in every request
apiClient.interceptors.request.use(
  (config) => {
    // Use the in-memory token
    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and token refresh
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Filter out login/refresh endpoints to avoid infinite loops
    // If the error is 401 and we haven't retried yet
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url.includes('/users/login') &&
      !originalRequest.url.includes('/users/refresh-token')
    ) {
      originalRequest._retry = true;

      try {
        // Attempt to refresh the token
        // We use a new axios instance or the same one but without the interceptor loop?
        // Actually, refresh-token endpoint doesn't need the access token, it needs the cookie.
        // And we don't want to trigger this interceptor again if it fails.
        // But the check !url.includes('/refresh-token') handles that.

        const res = await apiClient.post('/users/refresh-token');

        if (res.data.accessToken) {
          const newAccessToken = res.data.accessToken;
          setAccessToken(newAccessToken);

          // Update the header and retry the original request
          originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed (token expired or invalid)
        setAccessToken(null);
        // Clear user session if needed (AuthContext will likely catch the error from the original request eventually or we can emit)
        // For now, we reject the original error or the refresh error.
        // If we reject, the calling component (e.g. loadUser) will see the failure.
        return Promise.reject(refreshError);
      }
    }

    // Optional: Log errors only if critical, otherwise rely on UI handling
    // console.warn('[apiClient] Error', error.message);
    return Promise.reject(error);
  }
);

export default apiClient;